// src/components/common/TimeWheelPicker.tsx

import React, { useState, useEffect, useRef, useMemo } from 'react';

interface TimeWheelPickerProps {
  /** 當前選中的時間值 (HH:mm 格式，24小時制) */
  value: string;

  /** 時間變化回調 - 返回 HH:mm 格式（24小時制） */
  onChange: (value: string) => void;

  /** 點擊中央選項時觸發，用於確認選取並關閉 Modal */
  onConfirm?: () => void;

  /** 時間間隔（分鐘）- 預設 15 分鐘 */
  interval?: 5 | 10 | 15 | 30 | 60;

  /** 最小時間限制 (HH:mm 格式) */
  minTime?: string;

  /** 最大時間限制 (HH:mm 格式) */
  maxTime?: string;

  /** 使用 24 小時制顯示（預設 true） */
  use24Hours?: boolean;

  /** 自定義 className */
  className?: string;
}

// 單個選項的高度（像素）
const ITEM_HEIGHT = 48; // 對應 h-12

/**
 * iOS 風格滾輪時間選擇器
 * 使用 3D 變換和滾動吸附實現流暢的選擇體驗
 */
const TimeWheelPicker: React.FC<TimeWheelPickerProps> = ({
  value,
  onChange,
  onConfirm,
  interval = 15,
  minTime,
  maxTime,
  use24Hours = true,
  className = '',
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef(false);

  // 滾動到指定 index
  const scrollToIndex = (index: number, smooth = true) => {
    if (!scrollRef.current) return;
    if (smooth) {
      // smooth scroll：不設 isScrollingRef，讓 handleScroll 持續更新視覺
      scrollRef.current.scrollTo({
        top: index * ITEM_HEIGHT,
        behavior: 'smooth',
      });
    } else {
      // instant scroll：跳過 handleScroll 的處理
      isScrollingRef.current = true;
      scrollRef.current.scrollTop = index * ITEM_HEIGHT;
      setSelectedIndex(index);
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    }
  };

  // 生成時間選項列表
  const timeOptions = useMemo(() => {
    return generateTimeList(interval, minTime, maxTime);
  }, [interval, minTime, maxTime]);

  // 格式化時間顯示
  const formatTimeDisplay = (time24: string): string => {
    if (use24Hours) return time24;

    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // 初始化：滾動到當前值
  useEffect(() => {
    if (!scrollRef.current || !value) return;

    const index = timeOptions.indexOf(value);
    if (index >= 0) {
      // 初始化時不使用動畫
      scrollToIndex(index, false);
    }
  }, [value, timeOptions]);

  // 處理滾動事件（使用 setTimeout 實現 debounce）
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // 清除之前的 timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    const scrollTop = e.currentTarget.scrollTop;
    const rawIndex = scrollTop / ITEM_HEIGHT;
    const nearestIndex = Math.round(rawIndex);

    // 立即更新選中狀態（視覺反饋）- 僅用戶手動滾動時
    setSelectedIndex(nearestIndex);

    // Debounce: 100ms 後執行吸附和回調
    scrollTimeoutRef.current = setTimeout(() => {
      if (scrollRef.current && !isScrollingRef.current) {
        // 吸附到最近的選項
        const targetScrollTop = nearestIndex * ITEM_HEIGHT;
        if (Math.abs(scrollTop - targetScrollTop) > 1) {
          isScrollingRef.current = true;
          scrollRef.current.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth',
          });

          setTimeout(() => {
            isScrollingRef.current = false;
          }, 150);
        }

        // 觸發 onChange 回調
        if (timeOptions[nearestIndex]) {
          onChange(timeOptions[nearestIndex]);
        }
      }
    }, 100);
  };

  // 清理 timeout
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // 處理點擊時間選項
  const handleItemClick = (index: number) => {
    // 滾動中忽略點擊
    if (isScrollingRef.current) return;

    if (index === selectedIndex) {
      // 點擊中央選項 → 確認選取
      onConfirm?.();
    } else {
      // 點擊非中央選項 → 滾動到該位置（onChange 由 handleScroll 觸發）
      scrollToIndex(index);
    }
  };

  return (
    <div className={`relative h-[240px] overflow-hidden ${className}`}>
      {/* 選中指示器（中央高亮區域） */}
      <div
        className="absolute inset-x-0 top-1/2 -translate-y-1/2
                   h-12 border-y-2 border-sky-500 bg-sky-50 bg-opacity-30
                   pointer-events-none z-10"
        aria-hidden="true"
      />

      {/* 滾輪容器 */}
      <div
        ref={scrollRef}
        className="h-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
        onScroll={handleScroll}
        style={{
          perspective: '1000px',
          scrollbarWidth: 'none' /* Firefox */,
          msOverflowStyle: 'none' /* IE/Edge */,
        }}
        role="listbox"
        aria-label="Time selection"
        tabIndex={0}
      >
        {/* 上方填充（讓第一個選項能滾動到中央） */}
        <div className="h-[96px]" aria-hidden="true" />

        {/* 時間選項 */}
        {timeOptions.map((time, index) => {
          const offset = index - selectedIndex;
          const absOffset = Math.abs(offset);

          // 計算透明度（離中心越遠越淡）
          const opacity = Math.max(0.2, 1 - absOffset * 0.2);

          // 計算 3D 旋轉角度
          const rotateX = offset * 10; // 每個偏移量旋轉 10 度

          // 是否被選中
          const isSelected = index === selectedIndex;

          return (
            <div
              key={time}
              onClick={() => handleItemClick(index)}
              className={`
                h-12 flex items-center justify-center text-lg
                transition-all duration-150 snap-center cursor-pointer
                ${isSelected
                  ? 'font-semibold text-gray-900'
                  : 'font-medium text-gray-600 hover:text-gray-800'}
              `}
              style={{
                transform: `rotateX(${rotateX}deg) translateZ(0)`,
                opacity,
                willChange: 'transform, opacity',
              }}
              role="option"
              aria-selected={isSelected}
              data-time={time}
            >
              {formatTimeDisplay(time)}
            </div>
          );
        })}

        {/* 下方填充（讓最後一個選項能滾動到中央） */}
        <div className="h-[96px]" aria-hidden="true" />
      </div>

      {/* 頂部漸層遮罩 */}
      <div
        className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b
                   from-white to-transparent pointer-events-none"
        aria-hidden="true"
      />

      {/* 底部漸層遮罩 */}
      <div
        className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t
                   from-white to-transparent pointer-events-none"
        aria-hidden="true"
      />
    </div>
  );
};

/**
 * 生成時間列表（根據 interval 生成）
 */
function generateTimeList(
  interval: number,
  minTime?: string,
  maxTime?: string
): string[] {
  const times: string[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

      // 檢查時間範圍
      if (minTime || maxTime) {
        const timeMinutes = hour * 60 + minute;

        if (minTime) {
          const [minH, minM] = minTime.split(':').map(Number);
          const minMinutes = minH * 60 + minM;
          if (timeMinutes < minMinutes) continue;
        }

        if (maxTime) {
          const [maxH, maxM] = maxTime.split(':').map(Number);
          const maxMinutes = maxH * 60 + maxM;
          if (timeMinutes > maxMinutes) continue;
        }
      }

      times.push(timeStr);
    }
  }

  return times;
}

export default TimeWheelPicker;
